<template>
  <div class="decay">
    <!-- THE KERNEL THIS CHANNEL IS CARRYING. One bar per lag month: how much of a month's spend is
         delivered in the month it is bought, how much in the month after, and so on down the tail.

         AND IT IS PULLED BY THE TALLEST BAR. It was a row of fields, one per month, and a figure
         typed into a month is the wrong statement to let anybody make about a decay: the shape stops
         being a decay the moment the second month is answered — a month 4 above month 3 is a
         response no medium ever had — and the total is fixed, so every figure typed moves all the
         others and the user is asked to hold an arithmetic they have no reason to hold.

         What a decay actually is, is two figures: WHERE it peaks and HOW FAST it falls away. So the
         peak is the handle. Taken sideways it says which month the response peaks in; taken up and
         down it says how sharply it falls, the bar rising as more of the response lands at the peak
         and the tail growing as it drops. Every other bar follows, because the whole row is
         regenerated from the pair — see composeDecayFromShape — which is what keeps the shape a
         decay at every point of the gesture and the total exactly where it was. -->
    <div ref="plot" class="decay__plot" :class="{ 'decay__plot--dragging': dragging }">
      <!-- A COLUMN PER LAG, carrying its own figure over its own bar. The bars are normalised
           against their own tallest, so what the height says is the SHAPE; the share is written over
           each one because that is the only reading a single month has on its own. -->
      <div
        v-for="bar in bars"
        :key="bar.lag"
        class="decay__col"
        :class="{ 'decay__col--handle': bar.handle }"
        :style="bar.columnStyle"
        :title="bar.title"
        @pointerdown.prevent="bar.handle && onGrab($event)">
        <span class="decay__value">{{ bar.share }}<span class="decay__value-unit">%</span></span>
        <span class="decay__bar" :style="bar.barStyle"></span>
      </div>
      <!-- THE GRIP, sitting on the top edge of the peak's own bar. It is a button and not a
           decoration: everything this control does is on the two arrow axes as well, so a decay can
           be shaped without a pointer — left and right for the month it peaks in, up and down for
           how sharply it falls. Its hit area is taller than the pill that is drawn, the pill being
           small enough not to sit on top of the shape it is there to move. -->
      <button
        type="button"
        class="decay__grip"
        :style="gripStyle"
        :title="labels.drag"
        :aria-label="labels.drag"
        @pointerdown.prevent="onGrab"
        @keydown="onKey">
        <span class="decay__grip-pill"></span>
      </button>
    </div>
    <!-- The lag axis, a row of its own so a number stands under the bar it names without taking any
         of the height the bars are read against. -->
    <div class="decay__axis">
      <span v-for="bar in bars" :key="`axis_${bar.lag}`" class="decay__tick">{{ bar.lag }}</span>
    </div>
    <!-- WHAT THE HAND IS DOING, in the two figures it is doing it in. The line says how to move the
         shape until somebody moves it, and then it says what they have moved it to — which is also
         where the walls are felt: a peak dragged past month 2 or a fall pulled sharper than its own
         peak allows stops, and the reading stops with it while the pointer goes on travelling. -->
    <p class="decay__hint" :class="{ 'decay__hint--live': dragging }">
      <template v-if="reading">{{ readingText }}</template>
      <template v-else>{{ labels.drag }}</template>
    </p>

    <!-- EVERY SHAPE A DECAY CAN BE, drawn small and picked. The thumbnail is the label: the two
         figures say how a tile is filed, and the shape itself is what is recognised. The grid and
         the drag are one control at two grains — the tiles are the combinations a decay is usually
         stated in, and the drag reaches what is between them, landing back ON a tile and lighting it
         wherever the two agree.

         THE FIRST ROW IS THE TWO THAT ARE NOT COMBINATIONS, and it is unnamed because there is no
         peak that heads it: the channel's own, which is whatever the source model measured, and the
         custom one, which is wherever the shape has been dragged to. Everything below is one
         question asked three times, and those two are not that question. -->
    <div class="decay__shapes">
      <div class="decay__group">
        <span class="decay__group-name"></span>
        <div class="decay__row">
          <button
            v-if="sourceKernel"
            type="button"
            class="decay__shape decay__shape--named"
            :class="{ 'decay__shape--on': selected === sourceKey }"
            :aria-pressed="String(selected === sourceKey)"
            @click="$emit('pick', sourceKernel.slice())">
            <span class="decay__mini">
              <span
                v-for="bar in barsOf(sourceKernel)"
                :key="bar.lag"
                class="decay__mini-bar"
                :style="bar.style"></span>
            </span>
            <span class="decay__shape-name">{{ labels.imported }}</span>
          </button>
          <!-- A READING AND NOT A BUTTON, which is the one tile on the screen that is not pressable.
               It was the way into a row of per-month fields, and there are no fields any more: what
               it says now is that the shape on the plot is none of the tiles, which is a fact about
               the kernel rather than a place to go. Its thumbnail is that shape, so a reader dragging
               between two tiles watches it fill in. -->
          <span
            class="decay__shape decay__shape--named decay__shape--reading"
            :class="{ 'decay__shape--on': selected === customKey }">
            <span class="decay__mini">
              <span
                v-for="bar in barsOf(kernel)"
                :key="bar.lag"
                class="decay__mini-bar"
                :style="bar.style"></span>
            </span>
            <span class="decay__shape-name">{{ labels.custom }}</span>
          </span>
        </div>
      </div>

      <div v-for="group in groups" :key="group.peak" class="decay__group">
        <span class="decay__group-name">{{ groupName(group.peak) }}</span>
        <div class="decay__row">
          <button
            v-for="option in group.options"
            :key="option.key"
            type="button"
            class="decay__shape"
            :class="{ 'decay__shape--on': selected === option.key }"
            :aria-pressed="String(selected === option.key)"
            :title="shapeTitle(option)"
            :aria-label="shapeTitle(option)"
            @click="$emit('pick', option.kernel.slice())">
            <!-- THE SHAPE AND NOTHING ELSE. The tile carried its half-life under it, and a figure
                 under a picture is read as the picture's name: four tiles in a row came to four
                 numbers on a scale nobody stated the units of, and the reader compared 0.5 with 3
                 rather than a fast fall with a slow one. What the row is scanned for is the shape,
                 which is drawn; the two figures behind it are in the tooltip, where the one tile
                 being considered can be asked for them. -->
            <span class="decay__mini">
              <span
                v-for="bar in barsOf(option.kernel)"
                :key="bar.lag"
                class="decay__mini-bar"
                :style="bar.style"></span>
            </span>
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
// One channel's decay kernel: what it is now, every shape it could be instead, and the handle that
// pulls it between them.
//
// It owns no arithmetic about the payload — the templates are composeDecayTemplates', the tile that
// is lit is composeDecaySelected's, and the shape a drag arrives at is composeDecayFromShape's. That
// is not tidiness: this project cannot mount a component in a test (vue and vue-template-compiler
// are a patch apart, so @vue/test-utils refuses to load), so anything that lives here is untestable
// and anything a test has to pin lives in a util. What is left here is bars and pointers.
//
// THE SHARE IS THE READING, AND NOTHING HERE IS DENOMINATED. A kernel arrives in whatever units the
// source model measured it in, and its total is the one thing no gesture may move, so every weight
// on it is read as a share of that total — which is the only form in which a lag means anything on
// its own, and the reason the plot carries no figures on either axis.
import {
  composeDecayFromShape,
  composeDecayMoved,
  composeDecayShapeOf,
  DECAY_CUSTOM_KEY,
  DECAY_HALF_LIFE_MAX,
  DECAY_HALF_LIFE_STEP,
  DECAY_SOURCE_KEY
} from '@/utils/ekrmCompose.js'

// How much of the plot the tallest bar may take. The rest is the room its own figure stands in:
// every bar carries its share over it, and a kernel whose peak reached the top of the box would be
// one bar with nothing above it and a number floating off the chart. It costs nothing to read — the
// bars are normalised against their own tallest either way, so this scales all of them together and
// the shape is the shape.
const BAR_CEILING = 82

// WHAT THE HEIGHT OF THE PLOT IS WORTH, in months of half-life. The vertical drag is the whole
// range of the figure over the whole height of the box, so the gesture is the same size whatever
// the dialog has been resized to and a reader can always reach both ends without letting go. Two and
// a half months is the range itself — the ceiling less the fastest fall there is — and it puts the
// quarter-month step at a comfortable dozen-odd pixels on a plot of the usual height.
const DRAG_SPAN = DECAY_HALF_LIFE_MAX - 0.5

export default {
  name: 'DecayCurve',
  props: {
    // One weight per lag month, in the source model's own units — composeDecayKernel's shape.
    kernel: { type: Array, required: true },
    // [{ key, peak, halfLife, kernel }] — composeDecayTemplates, already at this channel's own total
    // and over its own months, so a tile and the plot above it are one picture at two sizes.
    templates: { type: Array, default: () => [] },
    // The decay the source model measured, for the tile that puts the channel back on it. Null on a
    // channel with nothing to go back to.
    sourceKernel: { type: Array, default: null },
    // Which tile is lit, or '' for a kernel that is none of them — composeDecaySelected.
    selected: { type: String, default: '' },
    // { lagAt, imported, custom, peak, shapeTitle, drag, now }. Every word this component draws
    // arrives here, the wizard owning the namespace they live in; lagAt, peak, shapeTitle and now
    // carry a {month}, {peak} or {half}.
    labels: { type: Object, required: true }
  },
  data () {
    return {
      // The gesture in progress, or null: where the pointer went down, the two figures the kernel
      // read as at that moment, and what a pixel is worth on each axis. The two figures are frozen
      // at the start and every move is measured from THERE rather than from the kernel on the
      // screen — a drag that re-read its own output would compound its own rounding, and a shape
      // held against a wall would creep once the pointer came back off it.
      drag: null,
      // The pair the last move arrived at, for the line under the axis. Held rather than derived so
      // that what it says is what was BUILT — the figures after the clamps — and not where the
      // pointer happens to be.
      reading: null
    }
  },
  computed: {
    sourceKey () {
      return DECAY_SOURCE_KEY
    },
    customKey () {
      return DECAY_CUSTOM_KEY
    },
    dragging () {
      return this.drag !== null
    },
    // WHERE THE HANDLE SITS: the month the payload says this channel peaks in. Which is the tallest
    // bar in every ordinary case and is deliberately not defined as "the tallest bar" — a measured
    // kernel may peak past month 2, and month 2 is where decayRange stops reading, so a handle on
    // month 5 would be one the first nudge teleported. It sits where the drag can actually stand.
    handleLag () {
      return composeDecayShapeOf(this.kernel).peak
    },
    // The big chart's own columns. Not barsOf's absolute bars: each lag is a full-height column with
    // its figure over its bar, so the two are laid out by the column rather than each positioned
    // against the box — which is what keeps a figure over a bar of no height from landing under the
    // axis. The thumbnails go on using barsOf, having no figures to make room for.
    bars () {
      const kernel = Array.isArray(this.kernel) ? this.kernel : []
      const width = 100 / Math.max(1, kernel.length)
      const total = kernel.reduce((sum, value) => sum + (Number(value) || 0), 0)
      const tallest = kernel.reduce((most, value) => Math.max(most, Number(value) || 0), 0)
      const handle = this.handleLag
      return kernel.map((raw, lag) => {
        const value = Number(raw) || 0
        const share = Math.round((total > 0 ? (value / total) * 100 : 0) * 10) / 10
        return {
          lag: lag,
          share: share,
          handle: lag === handle,
          columnStyle: {
            left: `calc(${lag * width}% + 1px)`,
            width: `calc(${width}% - 2px)`
          },
          barStyle: {
            flexBasis: `${tallest > 0 ? (value / tallest) * BAR_CEILING : 0}%`
          },
          title: `${this.labels.lagAt.replace('{month}', lag)}: ${share} %`
        }
      })
    },
    // The grip, over the handle column and on the top edge of its bar. Its own box is taller than
    // the pill inside it so that there is something to catch: a 4px line is a target nobody hits.
    gripStyle () {
      const bar = this.bars[this.handleLag]
      if (!bar) return { display: 'none' }
      const height = parseFloat(bar.barStyle.flexBasis) || 0
      return {
        left: bar.columnStyle.left,
        width: bar.columnStyle.width,
        bottom: `calc(${height}% - 11px)`
      }
    },
    readingText () {
      if (!this.reading) return ''
      return this.labels.now
        .replace('{peak}', this.reading.peak)
        .replace('{half}', this.roundHalf(this.reading.halfLife))
    },
    // The tiles by the month they peak in, which is the one of the two figures worth reading as a
    // row: a peak is a PLACE, and three of them are three families of shape, where the half-life is
    // a SPEED and reads as a run from fast to slow. So the grid is peaks down and speeds across, and
    // every tile in a row is one channel deciding how long to take.
    groups () {
      const order = []
      const byPeak = {}
      this.templates.forEach(template => {
        if (!byPeak[template.peak]) {
          byPeak[template.peak] = { peak: template.peak, options: [] }
          order.push(byPeak[template.peak])
        }
        byPeak[template.peak].options.push(template)
      })
      return order
    }
  },
  methods: {
    // A kernel as absolutely positioned bars in its own box, each against the tallest of them: what
    // is read off a decay is its SHAPE, and a channel delivering a fifth of its response in month
    // zero and one delivering three fifths would otherwise be drawn as two sizes of one picture.
    //
    // The THUMBNAILS only. The chart above them lays its bars out by column instead, because it
    // carries a figure over each one and a figure has to be laid out against the bar rather than
    // against the box — see `bars`.
    barsOf (values) {
      const kernel = Array.isArray(values) ? values : []
      const width = 100 / Math.max(1, kernel.length)
      const total = kernel.reduce((sum, value) => sum + (Number(value) || 0), 0)
      const tallest = kernel.reduce((most, value) => Math.max(most, Number(value) || 0), 0)
      return kernel.map((raw, lag) => {
        const value = Number(raw) || 0
        return {
          lag: lag,
          share: Math.round((total > 0 ? (value / total) * 100 : 0) * 10) / 10,
          style: {
            left: `calc(${lag * width}% + 1px)`,
            width: `calc(${width}% - 2px)`,
            bottom: 0,
            height: `${tallest > 0 ? (value / tallest) * 100 : 0}%`
          }
        }
      })
    },
    groupName (peak) {
      return this.labels.peak.replace('{month}', peak)
    },
    // The half-life as it is said out loud: a quarter of a month is the step, so two decimals hold
    // every figure the drag can reach, and a whole number is not dressed up as one.
    roundHalf (value) {
      return String(Math.round((Number(value) || 0) * 100) / 100)
    },
    // THE HAND ON THE PEAK. What is frozen here is the pair the kernel currently reads as and the
    // size of the box; the listeners go on the WINDOW rather than on the element, because the
    // element is the peak's own column and the peak is the thing being moved — a drag sideways
    // hands the grip to another column and takes the node the gesture started on out of the DOM.
    onGrab (event) {
      const plot = this.$refs.plot
      const kernel = Array.isArray(this.kernel) ? this.kernel : []
      if (!plot || kernel.length === 0) return
      const box = plot.getBoundingClientRect()
      const shape = composeDecayShapeOf(kernel)
      this.drag = {
        x: event.clientX,
        y: event.clientY,
        peak: shape.peak,
        halfLife: shape.halfLife,
        perColumn: Math.max(1, box.width / kernel.length),
        perMonth: DRAG_SPAN / Math.max(1, box.height)
      }
      this.reading = { peak: shape.peak, halfLife: shape.halfLife }
      window.addEventListener('pointermove', this.onMove)
      window.addEventListener('pointerup', this.onDrop)
      window.addEventListener('pointercancel', this.onDrop)
    },
    // ONE MOVE. Sideways is the month the response peaks in, a column at a time; up is a sharper
    // fall, which is what "more of it lands at the peak" comes to and is why the bar under the hand
    // follows the hand. The pair is handed to composeDecayFromShape, which holds it inside what a
    // decay can be and rebuilds the whole row at the total the channel already carries — so nothing
    // here can move what a month's spend is worth, only when it arrives.
    onMove (event) {
      if (!this.drag) return
      const acrossColumns = Math.round((event.clientX - this.drag.x) / this.drag.perColumn)
      const downMonths = (event.clientY - this.drag.y) * this.drag.perMonth
      const shape = composeDecayFromShape(
        this.kernel, this.drag.peak + acrossColumns, this.drag.halfLife + downMonths
      )
      if (!shape) return
      this.reading = { peak: shape.peak, halfLife: shape.halfLife }
      // A move that arrives where the kernel already is says nothing, and saying it would rebuild
      // the payload on every pixel of a gesture that is mostly held still.
      if (!composeDecayMoved(shape.kernel, this.kernel)) return
      this.$emit('shape', shape.kernel)
    },
    onDrop () {
      this.drag = null
      this.reading = null
      window.removeEventListener('pointermove', this.onMove)
      window.removeEventListener('pointerup', this.onDrop)
      window.removeEventListener('pointercancel', this.onDrop)
    },
    // THE SAME TWO AXES ON THE ARROW KEYS, so the shape can be stated without a pointer at all. It
    // steps from where the drag would start rather than from the kernel's raw reading: a measured
    // curve whose half-life is 1.4 steps to 1.25 and 1.75, on the grid the figure is stated on,
    // instead of dragging its own rounding along behind it.
    onKey (event) {
      const from = composeDecayShapeOf(this.kernel)
      const now = composeDecayFromShape(this.kernel, from.peak, from.halfLife)
      if (!now) return
      let peak = now.peak
      let halfLife = now.halfLife
      if (event.key === 'ArrowLeft') peak -= 1
      else if (event.key === 'ArrowRight') peak += 1
      else if (event.key === 'ArrowUp') halfLife -= DECAY_HALF_LIFE_STEP
      else if (event.key === 'ArrowDown') halfLife += DECAY_HALF_LIFE_STEP
      else return
      event.preventDefault()
      const shape = composeDecayFromShape(this.kernel, peak, halfLife)
      if (!shape || !composeDecayMoved(shape.kernel, this.kernel)) return
      this.$emit('shape', shape.kernel)
    },
    shapeTitle (option) {
      return this.labels.shapeTitle
        .replace('{peak}', option.peak)
        .replace('{half}', option.halfLife)
    }
  },
  // A gesture belongs to a mounted plot. Nothing here ends a drag when the dialog is closed under
  // one — the pointer is still down and the listeners are on the window — so they come off with the
  // component.
  beforeDestroy () {
    window.removeEventListener('pointermove', this.onMove)
    window.removeEventListener('pointerup', this.onDrop)
    window.removeEventListener('pointercancel', this.onDrop)
  }
}
</script>

<style scoped>
  /* THE ONE ELASTIC THING IN THE EDITOR. The card this sits in is the one allowed to take up
     whatever is left over when the two columns of the dialog are levelled, and this is what takes
     it: the tiles below are a fixed grid — ten thumbnails and their captions, which are the same
     size whatever the dialog is doing — and the plot is bars normalised against their own tallest,
     which read as the same shape at any height. The floor is where a shape stops being legible. */
  .decay {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
  }
  .decay__plot {
    position: relative;
    flex: 1 1 auto;
    /* Its natural height, and the floor it is given back when the column it is levelling has nothing
       to spare. Below this a ten-month kernel is a row of ticks rather than a shape, and there is no
       room over a bar for the figure that belongs to it. */
    min-height: 92px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    /* A drag that leaves the plot is still a drag — the listeners are on the window — and a browser
       that decides halfway through that the user is selecting text or panning the page would take
       the pointer away from it. */
    touch-action: none;
  }
  .decay__plot--dragging {
    cursor: grabbing;
    user-select: none;
  }
  .decay__axis,
  .decay__hint,
  .decay__shapes {
    flex: 0 0 auto;
  }
  /* ONE COLUMN PER LAG, the full height of the plot, with the figure and then the bar packed against
     its foot. The bar is sized by flex-basis rather than by `height` precisely so that the figure
     above it is laid out by the same box: a bar of no height still has its number standing over the
     axis, where an absolutely positioned label would have been under it. */
  .decay__col {
    position: absolute;
    top: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }
  /* The peak's own column is the handle as well as the grip on it, so the whole height of it can be
     taken hold of rather than a pill four pixels tall. */
  .decay__col--handle {
    cursor: grab;
  }
  .decay__plot--dragging .decay__col--handle {
    cursor: grabbing;
  }
  /* The purple the response wears throughout this wizard: this is what the media return, laid out
     over the months it arrives in. */
  .decay__bar {
    flex-grow: 0;
    flex-shrink: 0;
    width: 100%;
    background: #7E57C2;
    border-radius: 2px 2px 0 0;
    transition: background 120ms ease;
    /* A lag at nothing keeps a hairline, which is the honest picture of a month that delivers
       nothing and is what stops the axis looking as though it ended early. */
    min-height: 2px;
  }
  /* The bar that is held is the darker one, and it is darker whether the hand is on the column, on
     the grip, or on neither: it is the one the whole row is shaped from, and a reader who has not
     touched anything yet should already know which one that is. */
  .decay__col--handle .decay__bar {
    background: #5E35B1;
  }

  /* THE GRIP: a pill on the top edge of the peak's bar, in a box tall enough to be caught. What it
     says is that this edge is a thing that moves, which the cursor confirms and the line under the
     axis spells out. */
  .decay__grip {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 22px;
    padding: 0;
    background: none;
    border: 0;
    outline: none;
    cursor: grab;
  }
  .decay__plot--dragging .decay__grip {
    cursor: grabbing;
  }
  .decay__grip-pill {
    width: 60%;
    min-width: 14px;
    height: 4px;
    background: #5E35B1;
    border-radius: 2px;
    box-shadow: 0 0 0 2px #fff;
    transition: height 120ms ease, width 120ms ease;
  }
  .decay__grip:hover .decay__grip-pill,
  .decay__plot--dragging .decay__grip-pill {
    width: 80%;
    height: 6px;
  }
  .decay__grip:focus-visible .decay__grip-pill {
    box-shadow: 0 0 0 2px #fff, 0 0 0 4px rgba(94, 53, 177, 0.45);
  }

  /* THE SHARE, OVER THE BAR IT BELONGS TO. It is the one reading a lag has on its own — the bars are
     normalised against their own tallest, so the height says the shape and the number says the
     amount. */
  .decay__value {
    flex: 0 0 auto;
    padding-bottom: 2px;
    font-size: 0.62rem;
    line-height: 1.1;
    color: rgba(0, 0, 0, 0.5);
    text-align: center;
  }
  .decay__col--handle .decay__value {
    color: #5E35B1;
    font-weight: 600;
  }
  /* Lighter than the figure it follows, and tucked in: it is the same word eight times over and the
     figures are what differ. */
  .decay__value-unit {
    margin-left: 1px;
    opacity: 0.55;
  }

  .decay__axis {
    display: flex;
    padding-top: 3px;
  }
  .decay__tick {
    flex: 1 1 0;
    text-align: center;
    font-size: 0.62rem;
    color: rgba(0, 0, 0, 0.4);
  }

  /* One line, in one place, whether it is the instruction or the reading: they are the same sentence
     at two moments, and a reading that appeared somewhere else would move the eye off the shape at
     exactly the moment the shape is being moved. */
  .decay__hint {
    margin: 4px 0 0;
    min-height: 14px;
    font-size: 0.62rem;
    line-height: 1.2;
    color: rgba(0, 0, 0, 0.45);
  }
  .decay__hint--live {
    color: #5E35B1;
    font-weight: 600;
  }

  .decay__shapes {
    margin-top: 10px;
  }
  .decay__group {
    display: flex;
    align-items: center;
    margin-top: 6px;
  }
  /* The peak, named once for the row it heads. A fixed width, so the three rows of tiles line up
     under each other and a shape can be compared with the one above it. */
  .decay__group-name {
    flex: 0 0 54px;
    font-size: 0.6rem;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.45);
  }
  .decay__row {
    display: flex;
    flex: 1 1 auto;
  }
  .decay__row > * + * {
    margin-left: 6px;
  }

  .decay__shape {
    flex: 1 1 0;
    min-width: 0;
    padding: 4px 4px 2px;
    border: 1px solid rgba(0, 0, 0, 0.14);
    border-radius: 6px;
    background: #fff;
    outline: none;
    transition: border-color 120ms ease, background 120ms ease;
  }
  button.decay__shape:hover {
    border-color: rgba(126, 87, 194, 0.55);
    background: rgba(126, 87, 194, 0.04);
  }
  /* Lit by a border rather than by a fill. What is compared across ten tiles is the SHAPE inside
     each one, and a coloured ground under one of them would change what that shape is read against.
     The border thickens and the padding gives back exactly what it takes, so the tile does not grow
     and the row does not reflow as the choice moves along it. */
  .decay__shape--on {
    border-color: #7E57C2;
    border-width: 2px;
    padding: 3px 3px 1px;
    background: rgba(126, 87, 194, 0.06);
  }
  .decay__shape:focus-visible {
    box-shadow: 0 0 0 2px rgba(94, 53, 177, 0.35);
  }
  /* The one tile that is not pressed: it reports that the shape on the plot is none of the others.
     Dashed, and the cursor says nothing, so the row does not offer a click that has nowhere to go. */
  .decay__shape--reading {
    display: block;
    border-style: dashed;
  }
  /* The two on the unnamed row are captioned with a WORD where the rest carry a figure, so they get
     the width of two tiles apiece rather than of one. It is also what says they are not part of the
     run below: the row above a grid whose cells are all the same size, with cells that are not. */
  .decay__shape--named {
    flex: 1 1 0;
  }
  .decay__shape--named .decay__shape-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .decay__mini {
    position: relative;
    display: block;
    height: 22px;
  }
  .decay__mini-bar {
    position: absolute;
    background: rgba(126, 87, 194, 0.75);
    border-radius: 1px 1px 0 0;
    min-height: 1px;
  }
  .decay__shape--on .decay__mini-bar {
    background: #7E57C2;
  }
  .decay__shape-name {
    display: block;
    font-size: 0.62rem;
    color: rgba(0, 0, 0, 0.5);
    text-align: center;
  }
  .decay__shape--on .decay__shape-name {
    color: #5E35B1;
    font-weight: 600;
  }
</style>
